import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Plus, Upload, Trash2, X, Truck, Star, Sparkles, Package, CheckCircle2, BarChart3, Settings as SettingsIcon } from "lucide-react";
import Analytics from "../components/Analytics";
import Settings from "../components/Settings";
import { toast } from "sonner";

const CATEGORIES = ["tops", "bottoms", "outerwear", "dresses", "shoes", "accessories"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "OS"];
const CONDITIONS = ["New with tags", "Like new", "Excellent", "Good", "Fair"];

const EMPTY = {
  title: "", description: "", price: 0, size: "M",
  category: "tops", condition: "Excellent", brand: "", color: "",
  images: [], quantity: 1, featured: false,
};

const EMPTY_COUPON = { code: "", amount_off: 5, min_total: 0, expires_at: "", active: true };

export default function Admin() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [tab, setTab] = useState("inventory");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [shipFor, setShipFor] = useState(null);
  const [shipForm, setShipForm] = useState({ tracking_number: "", tracking_url: "" });
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [genLoading, setGenLoading] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/items", { params: { include_sold: true } }),
      api.get("/orders"),
      api.get("/coupons"),
    ]);
    setItems(a.data); setOrders(b.data); setCoupons(c.data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/items", { ...form, price: Number(form.price) });
      toast.success("Item added");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Deleted");
    load();
  };

  const submitShip = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/orders/${shipFor.id}/ship`, shipForm);
      toast.success("Marked shipped · email sent");
      setShipFor(null); setShipForm({ tracking_number: "", tracking_url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const markPacked = async (id) => {
    try {
      await api.patch(`/orders/${id}/pack`);
      toast.success("Packed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const markDelivered = async (id) => {
    try {
      await api.patch(`/orders/${id}/deliver`);
      toast.success("Delivered · follow-up email sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...couponForm, amount_off: Number(couponForm.amount_off), min_total: Number(couponForm.min_total || 0) };
      if (!payload.expires_at) payload.expires_at = null;
      await api.post("/coupons", payload);
      toast.success("Coupon created");
      setCouponForm(EMPTY_COUPON); setShowCoupon(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeCoupon = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    await api.delete(`/coupons/${id}`);
    load();
  };

  const regenMascots = async () => {
    if (!window.confirm("Regenerate Boggy and Fox mascots? Uses Emergent credits (~$0.05).")) return;
    setGenLoading(true);
    try {
      await api.post("/mascots/generate");
      toast.success("Mascots regenerated");
    } catch {
      toast.error("Generation failed");
    } finally { setGenLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="tag-sticker bg-pink text-xl inline-block mb-2 rotate-tilt-l">backroom</span>
          <h1 className="font-display text-5xl uppercase">Boggy & Fox HQ</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={regenMascots} disabled={genLoading} className="brut-btn bg-cyan px-4 py-3 flex items-center gap-2" data-testid="regen-mascots-btn">
            <Sparkles size={18} strokeWidth={3} /> {genLoading ? "Drawing…" : "Regen mascots"}
          </button>
          <button onClick={() => setShowForm(true)} className="brut-btn bg-orange text-white px-5 py-3 flex items-center gap-2" data-testid="new-item-btn">
            <Plus size={18} strokeWidth={3} /> New item
          </button>
        </div>
      </div>

      <div className="flex gap-2 mt-6 flex-wrap">
        <button onClick={() => setTab("inventory")} className={`brut-btn px-4 py-2 ${tab === "inventory" ? "bg-yellow" : "bg-white"}`} data-testid="tab-inventory">Inventory ({items.length})</button>
        <button onClick={() => setTab("orders")} className={`brut-btn px-4 py-2 ${tab === "orders" ? "bg-yellow" : "bg-white"}`} data-testid="tab-orders">Orders ({orders.length})</button>
        <button onClick={() => setTab("coupons")} className={`brut-btn px-4 py-2 ${tab === "coupons" ? "bg-yellow" : "bg-white"}`} data-testid="tab-coupons">Coupons ({coupons.length})</button>
        <button onClick={() => setTab("analytics")} className={`brut-btn px-4 py-2 flex items-center gap-1 ${tab === "analytics" ? "bg-yellow" : "bg-white"}`} data-testid="tab-analytics">
          <BarChart3 size={16} strokeWidth={3} /> Analytics
        </button>
        <button onClick={() => setTab("settings")} className={`brut-btn px-4 py-2 flex items-center gap-1 ${tab === "settings" ? "bg-yellow" : "bg-white"}`} data-testid="tab-settings">
          <SettingsIcon size={16} strokeWidth={3} /> Settings
        </button>
      </div>

      {tab === "inventory" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6">
          {items.map((it) => (
            <div key={it.id} className="brut p-3 flex flex-col gap-2 relative" data-testid={`admin-item-${it.id}`}>
              <div className="aspect-[4/5] overflow-hidden border-4 border-[#1A1A1A]">
                {it.images?.[0] ? <img src={fileUrl(it.images[0])} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full bg-[#f3efe6]" />}
              </div>
              <div className="font-display text-lg uppercase truncate">{it.title}</div>
              <div className="flex justify-between text-sm">
                <span>${Number(it.price).toFixed(2)}</span>
                <span>{it.size} · {it.condition}</span>
              </div>
              <div className="flex justify-between items-center gap-1 flex-wrap">
                {it.sold ? <span className="tag-sticker bg-pink text-sm">Sold</span> : <span className="tag-sticker bg-green text-sm">Live</span>}
                {it.featured && <span className="tag-sticker bg-cyan text-sm flex items-center gap-1"><Star size={12} strokeWidth={3} fill="currentColor" /> pick</span>}
                <button onClick={() => removeItem(it.id)} className="brut-btn bg-white px-2 py-1" data-testid={`delete-${it.id}`}><Trash2 size={16} strokeWidth={3} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="brut p-8 col-span-full text-center font-display text-2xl uppercase">No items yet</div>}
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 flex flex-col gap-3">
          {orders.length === 0 ? (
            <div className="brut p-8 text-center font-display text-2xl uppercase">No orders yet</div>
          ) : orders.map((o) => (
            <div key={o.id} className="brut p-5" data-testid={`admin-order-${o.id}`}>
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <div className="font-display text-xl uppercase">${Number(o.amount).toFixed(2)} · {o.status}</div>
                  <div className="text-sm opacity-70">{o.user_email} · {new Date(o.created_at).toLocaleString()}</div>
                  {o.coupon_code && <div className="text-sm">Coupon: <strong>{o.coupon_code}</strong> (-${Number(o.discount || 0).toFixed(2)})</div>}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <span className="tag-sticker bg-green text-lg">{o.items.length} items</span>
                  {!o.packed && !o.shipped && !o.delivered && (
                    <button onClick={() => markPacked(o.id)} className="brut-btn bg-yellow px-3 py-2 flex items-center gap-1 text-sm" data-testid={`pack-btn-${o.id}`}>
                      <Package size={16} strokeWidth={3} /> Mark packed
                    </button>
                  )}
                  {o.packed && !o.shipped && (
                    <button onClick={() => setShipFor(o)} className="brut-btn bg-orange text-white px-3 py-2 flex items-center gap-1 text-sm" data-testid={`ship-btn-${o.id}`}>
                      <Truck size={16} strokeWidth={3} /> Mark shipped
                    </button>
                  )}
                  {!o.packed && !o.shipped && (
                    <button onClick={() => setShipFor(o)} className="brut-btn bg-orange text-white px-3 py-2 flex items-center gap-1 text-sm" data-testid={`ship-btn-${o.id}`}>
                      <Truck size={16} strokeWidth={3} /> Skip & ship
                    </button>
                  )}
                  {o.shipped && !o.delivered && (
                    <button onClick={() => markDelivered(o.id)} className="brut-btn bg-green px-3 py-2 flex items-center gap-1 text-sm" data-testid={`deliver-btn-${o.id}`}>
                      <CheckCircle2 size={16} strokeWidth={3} /> Mark delivered
                    </button>
                  )}
                  {o.shipped && <span className="tag-sticker bg-cyan text-sm">{o.tracking_number}</span>}
                  {o.delivered && <span className="tag-sticker bg-green text-sm">Delivered</span>}
                </div>
              </div>
              <div className="text-sm mt-2">{o.items.map((it) => it.title).join(" · ")}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "coupons" && (
        <div className="mt-6">
          <button onClick={() => setShowCoupon(true)} className="brut-btn bg-orange text-white px-4 py-2 mb-4 flex items-center gap-2" data-testid="new-coupon-btn">
            <Plus size={16} strokeWidth={3} /> New coupon
          </button>
          {coupons.length === 0 ? (
            <div className="brut p-8 text-center font-display text-2xl uppercase">No coupons yet</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {coupons.map((c) => (
                <div key={c.id} className="brut p-4 flex justify-between items-center" data-testid={`coupon-${c.code}`}>
                  <div>
                    <div className="font-display text-2xl uppercase">{c.code}</div>
                    <div className="text-sm">${Number(c.amount_off).toFixed(2)} off · min ${Number(c.min_total).toFixed(2)}</div>
                    {c.expires_at && <div className="text-xs opacity-70">Expires {new Date(c.expires_at).toLocaleDateString()}</div>}
                  </div>
                  <button onClick={() => removeCoupon(c.id)} className="brut-btn bg-white px-2 py-1"><Trash2 size={16} strokeWidth={3} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "analytics" && <Analytics />}
      {tab === "settings" && <Settings />}

      {/* Item form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-[#1A1A1A]/60 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="new-item-modal">
          <form onSubmit={submit} className="brut bg-paper w-full max-w-2xl p-6 my-8 relative">
            <button type="button" onClick={() => setShowForm(false)} className="absolute -top-4 -right-4 brut-btn bg-white p-2"><X size={18} strokeWidth={3} /></button>
            <h2 className="font-display text-3xl uppercase mb-4">New piece</h2>

            <label className="block mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Title</span>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="brut-input w-full mt-1" data-testid="form-title" />
            </label>
            <label className="block mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Description</span>
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="brut-input w-full mt-1" data-testid="form-description" />
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Price ($)</span>
                <input type="number" min="1" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="brut-input w-full mt-1" data-testid="form-price" />
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Category</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="brut-input w-full mt-1" data-testid="form-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Size</span>
                <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="brut-input w-full mt-1" data-testid="form-size">
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Condition</span>
                <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="brut-input w-full mt-1" data-testid="form-condition">
                  {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Brand</span>
                <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="brut-input w-full mt-1" data-testid="form-brand" />
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Color</span>
                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="brut-input w-full mt-1" data-testid="form-color" />
              </label>
            </div>

            <label className="flex items-center gap-3 mt-4 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-5 h-5" data-testid="form-featured" />
              <span className="font-bold uppercase tracking-wider text-sm flex items-center gap-1">
                <Star size={16} strokeWidth={3} fill={form.featured ? "currentColor" : "none"} /> Mark as Fox's pick (staff pick)
              </span>
            </label>

            <div className="mt-4">
              <span className="text-sm font-bold uppercase tracking-wider">Curated by</span>
              <div className="flex gap-2 mt-2 flex-wrap" data-testid="form-curator">
                {[{k:null,l:"Neither"},{k:"boggy",l:"Boggy 🐸"},{k:"fox",l:"Fox 🦊"}].map((opt) => (
                  <button
                    key={opt.l}
                    type="button"
                    onClick={() => setForm({ ...form, curator: opt.k })}
                    className={`brut-btn px-4 py-2 text-sm ${form.curator === opt.k ? (opt.k === "boggy" ? "bg-green" : opt.k === "fox" ? "bg-orange text-white" : "bg-cyan") : "bg-white"}`}
                    data-testid={`form-curator-${opt.k || "none"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <span className="text-sm font-bold uppercase tracking-wider">Photos</span>
              <div className="flex flex-wrap gap-3 mt-2">
                {form.images.map((p, i) => (
                  <div key={i} className="brut-sm w-24 h-24 overflow-hidden relative">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 bg-pink text-white p-1"><X size={14} strokeWidth={3} /></button>
                  </div>
                ))}
                <label className="brut-sm w-24 h-24 flex flex-col items-center justify-center cursor-pointer bg-white" data-testid="upload-photo-btn">
                  <Upload size={20} strokeWidth={3} />
                  <span className="text-xs font-bold mt-1">{uploading ? "…" : "Upload"}</span>
                  <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                </label>
              </div>
            </div>

            <button type="submit" className="brut-btn bg-orange text-white px-6 py-3 mt-6 w-full" data-testid="form-submit">Add to inventory</button>
          </form>
        </div>
      )}

      {/* Ship modal */}
      {shipFor && (
        <div className="fixed inset-0 z-50 bg-[#1A1A1A]/60 flex items-center justify-center p-4" data-testid="ship-modal">
          <form onSubmit={submitShip} className="brut bg-paper w-full max-w-md p-6 relative">
            <button type="button" onClick={() => setShipFor(null)} className="absolute -top-4 -right-4 brut-btn bg-white p-2"><X size={18} strokeWidth={3} /></button>
            <h2 className="font-display text-2xl uppercase mb-1">Mark shipped</h2>
            <p className="text-sm opacity-70 mb-4">{shipFor.user_email} · ${Number(shipFor.amount).toFixed(2)}</p>
            <label className="block mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Tracking number</span>
              <input required value={shipForm.tracking_number} onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })} className="brut-input w-full mt-1" data-testid="ship-tracking" />
            </label>
            <label className="block mb-4">
              <span className="text-sm font-bold uppercase tracking-wider">Tracking URL (optional)</span>
              <input value={shipForm.tracking_url} onChange={(e) => setShipForm({ ...shipForm, tracking_url: e.target.value })} className="brut-input w-full mt-1" placeholder="https://..." data-testid="ship-url" />
            </label>
            <button type="submit" className="brut-btn bg-orange text-white px-6 py-3 w-full" data-testid="ship-submit">Ship it & email buyer</button>
          </form>
        </div>
      )}

      {/* Coupon modal */}
      {showCoupon && (
        <div className="fixed inset-0 z-50 bg-[#1A1A1A]/60 flex items-center justify-center p-4" data-testid="coupon-modal">
          <form onSubmit={submitCoupon} className="brut bg-paper w-full max-w-md p-6 relative">
            <button type="button" onClick={() => setShowCoupon(false)} className="absolute -top-4 -right-4 brut-btn bg-white p-2"><X size={18} strokeWidth={3} /></button>
            <h2 className="font-display text-2xl uppercase mb-4">New coupon</h2>
            <label className="block mb-3">
              <span className="text-sm font-bold uppercase tracking-wider">Code</span>
              <input required value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} className="brut-input w-full mt-1 uppercase" data-testid="coupon-form-code" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">$ off</span>
                <input type="number" min="1" step="0.01" required value={couponForm.amount_off} onChange={(e) => setCouponForm({ ...couponForm, amount_off: e.target.value })} className="brut-input w-full mt-1" data-testid="coupon-form-amount" />
              </label>
              <label className="block">
                <span className="text-sm font-bold uppercase tracking-wider">Min total</span>
                <input type="number" min="0" step="0.01" value={couponForm.min_total} onChange={(e) => setCouponForm({ ...couponForm, min_total: e.target.value })} className="brut-input w-full mt-1" data-testid="coupon-form-min" />
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-sm font-bold uppercase tracking-wider">Expires (optional)</span>
              <input type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })} className="brut-input w-full mt-1" data-testid="coupon-form-expires" />
            </label>
            <button type="submit" className="brut-btn bg-orange text-white px-6 py-3 mt-6 w-full" data-testid="coupon-submit">Create coupon</button>
          </form>
        </div>
      )}
    </div>
  );
}
